import { createFileRoute, Link } from '@tanstack/react-router'
import { ctaLinkClasses } from '@workspace/ui/components/dsm/cta-button'
import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { useAppContext } from '../../contexts/app-guard'

export const Route = createFileRoute('/app/evaluate/$startupId')({
  component: EvaluateRedirect,
})

/**
 * Auth gate + investor role check.
 * Redirects investor to /app/investor/$id/evaluate/$startupId.
 */
function EvaluateRedirect() {
  const { startupId } = Route.useParams()
  const { user, memberships } = useAppContext()

  const investor = memberships.find((m) => m.type === 'investor')
  const founderStartup = memberships.find((m) => m.type === 'startup')
  const dashboardHref =
    user.userType === 'FOUNDER' && founderStartup
      ? `/app/startup/${founderStartup.id}`
      : investor
        ? `/app/investor/${investor.id}`
        : '/app'

  useEffect(() => {
    if (user.userType !== 'INVESTOR' || !investor) return
    window.location.replace(`/app/investor/${investor.id}/evaluate/${startupId}`)
  }, [user.userType, investor, startupId])

  if (user.userType !== 'INVESTOR') {
    return (
      <ErrorState
        title="Investor access only"
        subtitle="This page is only available for investor accounts."
        action={
          <Link to={dashboardHref} className={ctaLinkClasses()}>
            <span>Go to dashboard</span>
          </Link>
        }
      />
    )
  }

  if (!investor) {
    return (
      <ErrorState
        title="No investor profile"
        subtitle="Please complete your investor onboarding first."
        action={
          <Link to={dashboardHref} className={ctaLinkClasses()}>
            <span>Go to dashboard</span>
          </Link>
        }
      />
    )
  }

  return null
}

function ErrorState({
  title,
  subtitle,
  action,
}: {
  title: string
  subtitle: string
  action: ReactNode
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-6 text-center">
        <div>
          <h1 className="text-xl font-semibold text-foreground">{title}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>
        </div>
        {action}
      </div>
    </div>
  )
}
